import axios from 'axios';
import { 
  NewExam, 
  Exam, 
  Course,
  Module,
  UserStats, 
  AuthResponse, 
  ExamSubmissionData,
  ExamSubmitResponse,
  UserListItem,
  DetailedUserStats,
  ExamAssignment,
  CourseAssignment,
  AssignmentData,
  TechnicianProfile,
  TechnicianCertification,
  CertificationUpload,
  ManagerTeamMember,
  ComplianceReport,
  Certification,
  TierRequirement,
  RevenueTracking
} from '../types';

export const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4005/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 300000, // 5 minutes timeout
  withCredentials: true
});

// Token management utilities
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (error?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  
  failedQueue = [];
};

const refreshToken = async (): Promise<string> => {
  try {
    const response = await axios.post(`${API_URL}/auth/refresh-token`, {}, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`
      }
    });
    
    const { token, user: refreshedUser, expiresAt } = response.data;
    
    const existingUser = localStorage.getItem('user');
    const mergedUser = existingUser
      ? { ...JSON.parse(existingUser), ...refreshedUser }
      : refreshedUser;
    
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(mergedUser));
    localStorage.setItem('tokenExpiresAt', expiresAt.toString());
    
    console.log('[Token Refresh] Token refreshed successfully, expires at:', new Date(expiresAt));
    return token;
  } catch (error) {
    console.error('[Token Refresh] Failed to refresh token:', error);
    throw error;
  }
};

const isTokenExpiringSoon = (minutesThreshold = 30): boolean => {
  const expiresAt = localStorage.getItem('tokenExpiresAt');
  if (!expiresAt) return true;
  
  const now = Date.now();
  const expirationTime = parseInt(expiresAt);
  const timeUntilExpiration = expirationTime - now;
  const thresholdMs = minutesThreshold * 60 * 1000;
  
  return timeUntilExpiration <= thresholdMs;
};

api.interceptors.request.use(
  async (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      if (isTokenExpiringSoon() && !isRefreshing && !config.url?.includes('/refresh-token')) {
        try {
          await refreshToken();
        } catch {
          // Continue with existing token, response interceptor handles expiration
        }
      }
      
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
    }

    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Auth endpoints must never be treated as an "expired session". A 401 here
// means bad credentials / unverified email / invalid refresh token, and the
// real message must reach the calling component instead of being masked by
// the token-refresh + "session expired" redirect below.
const AUTH_ENDPOINT_PATTERN = /\/auth\/(login|register|refresh-token|verify-email|resend-verification|forgot-password|reset-password)/;

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const isAuthEndpoint = AUTH_ENDPOINT_PATTERN.test(originalRequest?.url || '');

    // Handle 401 errors with automatic token refresh
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newToken = await refreshToken();
        processQueue(null, newToken);
        
        // Retry the original request with new token
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
        
      } catch (refreshError) {
        processQueue(refreshError, null);
        
        // Clear storage and redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('tokenExpiresAt');
        
        // Show user-friendly message
        const isScormRequest = originalRequest.url?.includes('/scorm/') || 
                              originalRequest.url?.includes('/modules/');
        
        if (isScormRequest) {
          alert('Your session has expired during the course. Your progress has been saved. Please log in again to continue.');
        } else {
          alert('Your session has expired. Please log in again.');
        }
        
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// Auth endpoints
export const login = (email: string, password: string) => {
  return api.post<AuthResponse>('/auth/login', { email, password });
};

export const register = (name: string, email: string, password: string, adminCode?: string) => {
  return api.post<AuthResponse>('/auth/register', { 
    name, 
    email, 
    password, 
    adminCode
  });
};

export const verifyEmail = (userId: string, verificationCode: string) => {
  return api.post<AuthResponse>('/auth/verify-email', { userId, verificationCode });
};

export const resendVerificationCode = (userId: string) => {
  return api.post('/auth/resend-verification', { userId });
};

export const forgotPassword = (email: string) => {
  return api.post('/auth/forgot-password', { email });
};

export const resetPassword = (token: string, password: string) => {
  return api.post('/auth/reset-password', { token, password });
};

export const refreshAuthToken = () => {
  return api.post('/auth/refresh-token');
};

export const validateToken = () => {
  return api.get('/auth/validate-token');
};

// User endpoints
export const getUserStats = (userId: number) => {
  return api.get<UserStats>(`/users/${userId}/stats`);
};

export const getAllUsers = () => {
  return api.get<UserListItem[]>('/users');
};

export const deleteUser = (userId: number) => {
  return api.delete(`/users/${userId}`);
};

export const getUserDetailedStats = (userId: number) => {
  return api.get<DetailedUserStats>(`/users/${userId}/stats`);
};

export const updateUserProfile = (userId: number, profileData: { branch: string; department: string }) => {
  return api.put(`/users/${userId}/profile`, profileData);
};

// Exam endpoints
export const getExams = () => {
  return api.get<Exam[]>('/exams');
};

export const getExam = (id: number) => {
  return api.get<Exam>(`/exams/${id}`);
};

export const createExam = (examData: NewExam) => {
  return api.post<{ message: string; exam: Exam }>('/exams', examData);
};

export const updateExam = (id: number, examData: NewExam) => {
  return api.put<{ message: string; exam: Exam }>(`/exams/${id}`, examData);
};

export const deleteExam = (id: number) => {
  return api.delete(`/exams/${id}`);
};

export const submitExam = (examId: number, submissionData: ExamSubmissionData) => {
  return api.post<ExamSubmitResponse>(`/exams/${examId}/submit`, submissionData);
};

// Course endpoints
export const getCourses = () => {
  return api.get<Course[]>('/courses');
};

export const getCoursesWithProgress = () => {
  return api.get<Course[]>('/courses/with-progress');
};

export const getCourse = (id: number) => {
  return api.get<Course>(`/courses/${id}`);
};

export const createCourse = (formData: FormData) => {
  return api.post<{ message: string; course: Course }>('/courses', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    timeout: 600000 // 10 minutes for large file uploads
  });
};

export const updateCourse = (id: number, formData: FormData) => {
  return api.put<{ message: string; course: Course }>(`/courses/${id}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    timeout: 600000 // 10 minutes for large file uploads
  });
};

// Module progress API functions
export const getModuleProgress = (courseId: number) => {
  return api.get<{ courseId: number; modules: Module[] }>(`/modules/course/${courseId}/progress`);
};

export const updateModuleProgress = (moduleId: number, progress: number, courseId: number) => {
  return api.put<{ message: string; moduleProgress: any }>(`/modules/${moduleId}/progress`, {
    progress,
    courseId
  });
};

export const completeModule = (moduleId: number, courseId: number) => {
  return api.put<{ message: string; moduleProgress: any }>(`/modules/${moduleId}/complete`, {
    courseId
  });
};

export const deleteCourse = (id: number) => {
  return api.delete(`/courses/${id}`);
};

// Course enrollment endpoints
export const enrollInCourse = (courseId: number) => {
  return api.post(`/courses/${courseId}/enroll`);
};

export const updateCourseProgress = (courseId: number, progress: number) => {
  return api.put(`/courses/${courseId}/progress`, { progress });
};

export const completeCourse = (courseId: number) => {
  return api.put(`/courses/${courseId}/complete`);
};

export const getCourseProcessingStatus = (courseId: number) => {
  return api.get(`/courses/${courseId}/processing-status`);
};

// Exam assignment endpoints
export const assignExamToUsers = (examId: number, assignmentData: AssignmentData) => {
  return api.post<{ message: string; assignments: ExamAssignment[] }>(`/exams/${examId}/assign`, assignmentData);
};

export const getExamAssignments = (examId: number) => {
  return api.get<ExamAssignment[]>(`/exams/${examId}/assignments`);
};

export const removeExamAssignment = (examId: number, userId: number) => {
  return api.delete(`/exams/${examId}/assign/${userId}`);
};

// Course assignment endpoints
export const assignCourseToUsers = (courseId: number, assignmentData: AssignmentData) => {
  return api.post<{ message: string; assignments: CourseAssignment[] }>(`/courses/${courseId}/assign`, assignmentData);
};

export const getCourseAssignments = (courseId: number) => {
  return api.get<CourseAssignment[]>(`/courses/${courseId}/assignments`);
};

export const removeCourseAssignment = (courseId: number, userId: number) => {
  return api.delete(`/courses/${courseId}/assign/${userId}`);
};

// SCORM Editor API functions
export const analyzeScormModule = (moduleId: number) => {
  return api.post('/scorm-editor/analyze', { moduleId });
};

export const editScormTiming = (moduleId: number, videoFile: string, duration: string, slideId?: string, slideName?: string) => {
  return api.post('/scorm-editor/edit-timing', { moduleId, videoFile, duration, slideId, slideName });
};

export const updateSlideName = (moduleId: number, slideId: string, slideName: string) => {
  return api.post('/scorm-editor/edit-timing', { moduleId, slideId, slideName });
};

export const getScormBackups = (moduleId: number) => {
  return api.get(`/scorm-editor/backups/${moduleId}`);
};

export const restoreScormBackup = (moduleId: number, backupId: number) => {
  return api.post('/scorm-editor/restore', { moduleId, backupId });
};

// Pre-upload SCORM Editor API functions
export const analyzePreUploadScorm = (file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/pre-upload-scorm/analyze', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
};

export const editPreUploadScorm = (file: File, edits: any) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('edits', JSON.stringify(edits));
  return api.post('/pre-upload-scorm/edit', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    responseType: 'blob'
  });
};

export const validatePreUploadScorm = (file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/pre-upload-scorm/validate', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
};

export const getPreUploadScormInfo = () => {
  return api.get('/pre-upload-scorm/info');
};

// Technician Certification API functions
export const getTechnicianProfile = (userId: number) => {
  return api.get<TechnicianProfile>(`/technician/profile/${userId}`);
};

export const getTechnicianCertifications = (userId: number) => {
  return api.get<TechnicianCertification[]>(`/technician/certifications/${userId}`);
};

export const getAvailableCertifications = (userId: number) => {
  return api.get<Certification[]>(`/technician/certifications/available/${userId}`);
};

export const addCertificationToProfile = (certificationData: {
  certification_id: number;
  certification_number: string;
  obtained_date?: string;
  expiration_date?: string;
}) => {
  return api.post<{ message: string; certification: TechnicianCertification }>('/technician/certifications/add', certificationData);
};

export const uploadCertification = (certificationData: CertificationUpload) => {
  const formData = new FormData();
  formData.append('certification_id', certificationData.certification_id.toString());
  formData.append('file', certificationData.file);
  formData.append('obtained_date', certificationData.obtained_date.toISOString());
  if (certificationData.expiration_date) {
    formData.append('expiration_date', certificationData.expiration_date.toISOString());
  }
  if (certificationData.notes) {
    formData.append('notes', certificationData.notes);
  }
  
  return api.post<{ message: string; certification: TechnicianCertification }>('/technician/certifications/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
};

export const pauseNotifications = (userId: number, pauseUntil: Date) => {
  return api.post('/technician/pause-notifications', { userId, pauseUntil });
};

export const resumeNotifications = (userId: number) => {
  return api.post('/technician/resume-notifications', { userId });
};

// Manager API functions
export const getManagerTeam = (filters?: { branch?: string; department?: string }) => {
  return api.get<{ teamMembers: ManagerTeamMember[]; summary: { total_members: number; avg_completion: number; total_warnings: number; } }>('/manager/team', { params: filters });
};

export const getTeamMemberDetails = (userId: number) => {
  return api.get<TechnicianProfile>(`/manager/team/${userId}`);
};

export const overrideCertification = (certId: number, data: { obtained_date?: Date; expiration_date?: Date; notes?: string }) => {
  return api.put(`/manager/override/${certId}`, data);
};

export const updateRevPayRatio = (userId: number, ratio: number) => {
  return api.put(`/manager/rev-pay/${userId}`, { revPayRatio: ratio });
};

export const submitTierPromotion = (userId: number, data: { newTier: number; justification: string }) => {
  return api.post('/manager/tier-promotion', { userId, ...data });
};

// Admin Certification API functions
export const getTierStructure = () => {
  return api.get<TierRequirement[]>('/certifications/tier-requirements');
};

export const getTierRequirements = () => {
  return api.get<TierRequirement[]>('/certifications/tier-requirements');
};

export const addTierRequirement = (data: { tier_level: number; department: string; certification_id: number }) => {
  return api.post<TierRequirement>('/certifications/tier-requirements', data);
};

export const updateTierRequirement = (id: number, data: Partial<TierRequirement>) => {
  return api.put<TierRequirement>(`/certifications/tier-requirements/${id}`, data);
};

export const removeTierRequirement = (id: number) => {
  return api.delete(`/certifications/tier-requirements/${id}`);
};

export const getCertifications = () => {
  return api.get<Certification[]>('/admin/certifications');
};

export const createCertification = (data: Partial<Certification>) => {
  return api.post<Certification>('/admin/certifications', data);
};

export const updateCertification = (id: number, data: Partial<Certification>) => {
  return api.put<Certification>(`/admin/certifications/${id}`, data);
};

export const deactivateCertification = (id: number) => {
  return api.delete(`/admin/certifications/${id}`);
};

export const bulkImportCertifications = (file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  
  return api.post('/admin/certifications/bulk-import', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
};

// Revenue API functions
export const uploadRevenueData = (file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  
  return api.post('/revenue/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
};

export const getRevenueLeaderboard = (period: 'monthly' | 'quarterly' | 'yearly', date?: string) => {
  return api.get('/revenue/leaderboard', { params: { period, date } });
};

export const getUserRevenue = (userId: number) => {
  return api.get<{
    user: any;
    revenue_history: RevenueTracking[];
    totals: any;
    averages: any;
    recent_badges: any[];
    period: any;
  }>(`/revenue/user/${userId}`);
};

export const addManualRevenueEntry = (data: { userId: number; month: Date; grossRevenue: number; payAmount?: number }) => {
  return api.post('/revenue/manual-entry', data);
};

// Reports API functions
export const getExpiringCertifications = (days?: number) => {
  return api.get<TechnicianCertification[]>('/reports/certifications/expiring', { params: { days } });
};

export const getComplianceReport = (branch?: string, department?: string) => {
  return api.get<ComplianceReport[]>('/reports/certifications/compliance', { params: { branch, department } });
};

export const sendCertificationReminder = (certificationId: number) => {
  return api.post(`/reports/certifications/send-reminder`, { certificationId });
};

export const exportCertificationData = (format: 'excel' | 'csv', filters?: any) => {
  return api.get('/reports/certifications/export', { 
    params: { format, ...filters },
    responseType: 'blob'
  });
};

export default api;